<template>
  <div
    id="app"
    class="app"
  >
    <div style="z-index: 1000000">
      <div
        v-show="!modalActive && !trainingState.firstTutorialMessage"
      >
        <countdown-component />
        <hud-component v-show="debugState.isHudActive" />
        <error-modal
          class="pointer-events-auto"
          @close="closeApp"
        />
        <emotion-messages />
        <start-stats-box />
        <text-message
          v-if="textMessageState.showMessage"
          :style="textMessageStyle"
          :first-line-text="textMessageState.firstLineText"
          :first-line-sprite-type="textMessageState.firstLineTextType"
          :second-line-text-type="textMessageState.secondLineTextType"
          :second-line-left-number="textMessageState.secondLineLeftNumber"
          :show-first-line="textMessageState.showFirstLine"
          :show-second-line="textMessageState.showSecondLine"
          :with-animation="true"
          :show-type="textMessageState.showType"
          :delay-to-hide="0.3"
          :duration-hide="[0.5, 0.5, 0.5, 0.5]"
          :duration-translation="[0.5, 0.5, 0.5 * 2, 0.5]"
          @finished-first-line="finishedFirstLine"
          @finished-second-line="finishedSecondLine"
        />
        <text-message-translatable
          v-if="textMessageTranslatableState.show"
          :style="textMessageTranslatableStyle"
          :text="$t(textMessageTranslatableState.text)"
          :type="textMessageTranslatableState.type"
          @finished-text="finishedText"
        />
        <text-message-meters v-if="textMessageMetersState.showMessage" />
        <boxes-records />
        <phase-start />
        <start-debug-bar />
        <under-menu-component />
        <info-component />
        <inputs-overlay />
        <efficiency-component :is-mobile="isMobile" />
        <angle-status />
        <angle-info />
        <training-layout v-if="uiState.showTrainingLayout" />
        <training-results v-if="trainingResultsState.showResults" />
        <attempts-table-component />
        <data-table />
        <mini-table
          v-if="miniTableState.show"
          style="position:absolute; left:1%; top: 1%; margin: 0.25em 0px;"
          :rows-data="miniTableState.rowsData"
        />
        <black-overlay />
      </div>
      <fps-component />
      <tutorial-logic-component v-show="!modalActive" />
      <menu-section
        v-if="showMenu && !trainingState.firstTutorialMessage"
        :is-mobile="isMobile"
        style="pointer-events: auto"
        :is-app-ssm="isAppSSM2023"
        :show-overlay="modalActive"
        @toggle-ui="toggleUi"
      />
      <color-overlay-component
        v-if="blurState.isActive && !blurState.isToggle"
        :opacity="0.6"
      />
      <loading-circle-overlay />
      <training-override
        style="pointer-events: auto"
        @mousedown.stop=""
        @mouseup.stop=""
        @touchstart.stop=""
        @touchend.stop=""
      />
    </div>
    <loading />
    <white-shine-component />
  </div>
</template>

<script lang="ts">
// External Package codes

// Internal Package codes
import {
  game,
  MobileDetector,
  requestManager,
  modes,
  inputsManager
} from '@powerplay/core-minigames'
import {
  ErrorModal,
  LoadingCircleOverlay,
  ColorOverlayComponent,
  TextMessage,
  TextMessageTranslatable,
  loadingState,
  loadingCircleState,
  MiniTable,
} from '@powerplay/core-minigames-ui-ssm'

// Component codes

// Debug Components
import HudComponent from '@/components/DebugComponents/HUD.vue'
// import DirectionsInfo from '@/components/DebugComponents/DirectionsInfo.vue'

// Game Components
import InputsOverlay from '@/components/Inputs/InputsOverlay.vue'
import PhaseStart from '@/components/PhaseStart.vue'
import DataTable from '@/components/Table/DataTable.vue'
import Loading from '@/components/Loading.vue'
import InfoComponent from '@/components/InfoComponent.vue'
import MenuSection from '@/components/MenuSection/index.vue'
import EfficiencyComponent from '@/components/Inputs/EfficiencyComponent.vue'
import FpsComponent from '@/components/FpsComponent.vue'
import AngleInfo from '@/components/AngleInfo.vue'
import TrainingLayout from '@/components/Training/TrainingLayout.vue'
import TrainingResults from '@/components/Training/TrainingResults.vue'
import UnderMenuComponent from '@/components/UnderMenuComponent/index.vue'
import TutorialLogicComponent from '@/components/Tutorial/TutorialLogicComponent.vue'
import CountdownComponent from '@/components/CountdownComponent/index.vue'
import TrainingOverride from '@/components/Training/TrainingOverride.vue'
import WhiteShineComponent from '@/components/WhiteShineComponent.vue'
import StartDebugBar from '@/components/StartDebugBar.vue'
import BlackOverlay from '@/components/BlackOverlay.vue'
import BoxesRecords from '@/components/BoxesRecords.vue'
import StartStatsBox from '@/components/StartStatsBox.vue'
import AttemptsTableComponent from '@/components/AttemptsTableComponent.vue'
import TextMessageMeters from '@/components/TextMessageMeters.vue'

// Internal Codes
import { initiateListeners } from '@/helpers/initiateListeners'
import { tutorialUIChange } from '@/app/modes/tutorial/TutorialUIChange'
import { endManager } from '@/app/EndManager'

import { defineComponent } from 'vue'
import EmotionMessages from '../EmotionMessages.vue'
import { startPhaseStateManager } from '@/app/phases/StartPhase/StartPhaseStateManager'
import {
  blurState,
  type BlurState,
  uiState,
  debugState,
  trainingResultsState,
  trainingState,
  textMessageState,
  miniTableState,
  textMessageTranslatableState,
  textMessageMetersState,
} from '@/stores'
import AngleStatus from '../Inputs/AngleStatus.vue'


export default defineComponent({
  name: 'StructureComponent',
  components: {
    HudComponent,
    // DirectionsInfo,
    Loading,
    InputsOverlay,
    PhaseStart,
    MenuSection,
    ErrorModal,
    DataTable,
    InfoComponent,
    EfficiencyComponent,
    AngleInfo,
    FpsComponent,
    TrainingLayout,
    TrainingResults,
    UnderMenuComponent,
    TutorialLogicComponent,
    CountdownComponent,
    LoadingCircleOverlay,
    TrainingOverride,
    ColorOverlayComponent,
    WhiteShineComponent,
    StartDebugBar,
    BlackOverlay,
    EmotionMessages,
    TextMessage,
    TextMessageTranslatable,
    BoxesRecords,
    MiniTable,
    StartStatsBox,
    AngleStatus,
    AttemptsTableComponent,
    TextMessageMeters
  },
  data() {

    return {
      modalActive: false,
      isAppSSM2023: false,
      uiState: uiState(),
      debugState: debugState(),
      trainingState: trainingState(),
      blurState: blurState(),
      trainingResultsState: trainingResultsState(),
      textMessageState: textMessageState(),
      miniTableState: miniTableState(),
      textMessageTranslatableState: textMessageTranslatableState(),
      textMessageMetersState: textMessageMetersState(),
    }

  },
  computed: {
    isMobile(): boolean {

      return MobileDetector.isMobile()

    },
    isTutorial(): boolean {

      return modes.isTutorial()

    },
    showMenu(): boolean {

      return !this.showLoading() && !this.trainingResultsState.showResults

    },
    textMessageStyle(): Record<string, string> | undefined {

      return {
        position: 'absolute',
        left: '50%',
        top: '20%',
        'transform-origin': 'top'
      }

    },
    textMessageTranslatableStyle(): Record<string, string> | undefined {

      return {
        position: 'absolute',
        left: '50%',
        top: `${textMessageTranslatableState().top}%`,
        'transform-origin': 'top'
      }

    },
  },
  watch: {
    blurState: {
      handler(value: BlurState) {

        const blurClass = modes.isTutorial() ? 'blur-class-v2' : 'blur-class'

        if (value.isActive) {

          document.getElementsByTagName('canvas')[0].classList.add(blurClass)

        } else {

          document.getElementsByTagName('canvas')[0].classList.remove(blurClass)

        }

        // osetrenie vypnutia menu pocas zobrazenej tabulky
        if (!value.isActive && value.isTable) {

          blurState().isActive = true

        }

      },
      deep: true
    },
  },
  created(): void {

    loadingState().$patch({
      showLoading: true,
      loadingProgress: 0
    })

    this.isAppSSM2023 = modes.isAppWSM2021()
    initiateListeners(this)
    tutorialUIChange.registerVM(this)

  },
  methods: {
    showLoading(): boolean {

      return loadingState().showLoading

    },
    closeApp(): void {

      endManager.prematureEnded = true
      loadingCircleState().isActive = true
      requestManager.redirect(true)

    },
    toggleUi(toggle = true) {

      if (toggle) {

        blurState().isActive = true
        blurState().isToggle = true
        inputsManager.actionInputsBlocked = true
        this.modalActive = true
        game.pauseGame()

      } else {

        inputsManager.actionInputsBlocked = false
        this.modalActive = false
        blurState().isActive = false
        blurState().isToggle = false
        game.resumeGame()

      }

    },
    finishedFirstLine() {

      startPhaseStateManager.hideTextMessage(1)

    },
    finishedSecondLine() {

      startPhaseStateManager.hideTextMessage(2)

    },
    finishedText() {

      textMessageTranslatableState().show = false

    }
  }
})
</script>

<style lang="less">
.blur-class {
  filter: blur(4px);
}
.blur-class-v2 {
  filter: blur(8px);
}
.app {
  min-height: 100%;
  min-width: 100%;
  pointer-events: none;
}

.pointer-events-auto {
  pointer-events: auto;
}

.absolute {
  position: absolute;
}

.midder {
  top: 50%;
  left: 50%;
}

.mini-table {
  .mini-table-row {
    .time {
      width: 110px;
    }
    &.is-longer {
      .time {
        right: 100px;
      }
    }
    .row-i {
      .last-attempt {
        width: 100px;
      }
    }
  }
}
</style>
